import React, { useState } from 'react';
import { Building2, ArrowRight, AlertTriangle } from 'lucide-react';
import { useAuth } from '../lib/AuthContext';

// Shown when a user is authenticated but has no org_id yet.
// This happens on first login before an admin has assigned them to an org,
// OR for the very first admin who needs to create the org.

const OrgSetup = () => {
  const { createOrganisation, signOut } = useAuth();
  const [name, setName]       = useState('');
  const [slug, setSlug]       = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);

  const handleNameChange = (value) => {
    setName(value);
    // Auto-generate slug from name
    setSlug(value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, ''));
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!name.trim() || !slug.trim()) return;
    setLoading(true);
    setError(null);
    try {
      await createOrganisation(name.trim(), slug.trim());
    } catch (err) {
      setError(err.message || 'Failed to create organisation.');
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%)',
      padding: '1.5rem',
    }}>
      <div style={{
        width: '100%',
        maxWidth: 440,
        background: 'white',
        borderRadius: '1rem',
        padding: '2.5rem 2rem',
        boxShadow: '0 25px 50px -12px rgba(0,0,0,0.4)',
      }}>
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <div style={{
            width: 56, height: 56,
            background: 'linear-gradient(135deg, #2563eb, #1d4ed8)',
            borderRadius: '0.75rem',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: '1rem',
          }}>
            <Building2 style={{ width: 28, height: 28, color: 'white' }} />
          </div>
          <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#0f172a', margin: 0 }}>
            Set up your organisation
          </h1>
          <p style={{ color: '#64748b', fontSize: '0.875rem', marginTop: '0.5rem', lineHeight: 1.6 }}>
            You're the first person here. Create your organisation to get started — you'll be set as the admin.
          </p>
        </div>

        <form onSubmit={handleCreate} style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
          <div>
            <label style={{ fontSize: '0.875rem', fontWeight: 600, color: '#374151', display: 'block', marginBottom: '0.4rem' }}>
              Organisation name
            </label>
            <input
              type="text"
              value={name}
              onChange={e => handleNameChange(e.target.value)}
              placeholder="Acme Auto Group"
              required
              style={{
                width: '100%', padding: '0.75rem',
                border: '1px solid #e2e8f0', borderRadius: '0.5rem',
                fontSize: '1rem', color: '#0f172a', outline: 'none',
                boxSizing: 'border-box', fontFamily: 'inherit',
              }}
            />
          </div>

          <div>
            <label style={{ fontSize: '0.875rem', fontWeight: 600, color: '#374151', display: 'block', marginBottom: '0.4rem' }}>
              Organisation ID
              <span style={{ fontWeight: 400, color: '#94a3b8', marginLeft: '0.5rem' }}>auto-generated</span>
            </label>
            <input
              type="text"
              value={slug}
              onChange={e => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
              placeholder="acme-auto-group"
              required
              style={{
                width: '100%', padding: '0.75rem',
                border: '1px solid #e2e8f0', borderRadius: '0.5rem',
                fontSize: '0.9rem', color: '#64748b', outline: 'none',
                boxSizing: 'border-box', fontFamily: 'monospace',
                background: '#f8fafc',
              }}
            />
            <p style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: '0.3rem' }}>
              Lowercase letters, numbers and hyphens only. Cannot be changed later.
            </p>
          </div>

          {error && (
            <div style={{
              display: 'flex', alignItems: 'flex-start', gap: '0.5rem',
              padding: '0.75rem', background: '#fef2f2', border: '1px solid #fecaca',
              borderRadius: '0.5rem', color: '#dc2626', fontSize: '0.875rem',
            }}>
              <AlertTriangle style={{ width: 16, height: 16, flexShrink: 0, marginTop: 1 }} />
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading || !name.trim() || !slug.trim()}
            style={{
              padding: '0.875rem', borderRadius: '0.5rem', border: 'none',
              background: loading || !name.trim() ? '#93c5fd' : 'linear-gradient(135deg, #2563eb, #1d4ed8)',
              color: 'white', fontSize: '1rem', fontWeight: 600,
              cursor: loading || !name.trim() ? 'not-allowed' : 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem',
              fontFamily: 'inherit',
            }}
          >
            {loading ? 'Creating...' : (
              <>Create organisation <ArrowRight style={{ width: 18, height: 18 }} /></>
            )}
          </button>
        </form>

        <p style={{ textAlign: 'center', fontSize: '0.8rem', color: '#94a3b8', marginTop: '1.5rem' }}>
          Signed in with the wrong account?{' '}
          <button onClick={signOut} style={{ background: 'none', border: 'none', color: '#2563eb', cursor: 'pointer', fontSize: '0.8rem', padding: 0, fontFamily: 'inherit' }}>
            Sign out
          </button>
        </p>
      </div>
    </div>
  );
};

export default OrgSetup;
